import React from "react";
import {
  Box,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Stack,
  Typography,
  Chip,
  FormHelperText,
} from "@mui/material";
import {
  Gavel as GavelIcon,
  Public as PublicIcon,
  Shield as ShieldIcon,
  Balance as BalanceIcon,
  Factory as FactoryIcon,
} from "@mui/icons-material";

interface AuthorityCheckboxGroupProps {
  label: string;
  value: string[];
  onChange: (value: string[]) => void;
  options: string[];
  helperText?: string;
  disabled?: boolean;
}

const getAuthorityIcon = (authority: string) => {
  switch (authority) {
    case 'MFN':
      return <PublicIcon sx={{ fontSize: 18 }} />;
    case 'SECTION_301':
      return <GavelIcon sx={{ fontSize: 18 }} />;
    case 'SECTION_232':
      return <FactoryIcon sx={{ fontSize: 18 }} />;
    case 'SECTION_201':
      return <ShieldIcon sx={{ fontSize: 18 }} />;
    case 'IEEPA-Reciprocal':
    case 'IEEPA-Fent':
      return <ShieldIcon sx={{ fontSize: 18 }} />;
    case 'AD':
    case 'CVD':
      return <BalanceIcon sx={{ fontSize: 18 }} />;
    default:
      return null;
  }
};

const getAuthorityColor = (authority: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
  switch (authority) {
    case 'MFN':
      return 'primary';
    case 'SECTION_301':
      return 'warning';
    case 'SECTION_232':
      return 'error';
    case 'SECTION_201':
      return 'secondary';
    case 'IEEPA-Reciprocal':
      return 'secondary';
    case 'IEEPA-Fent':
      return 'error';
    case 'AD':
    case 'CVD':
      return 'success';
    default:
      return 'default';
  }
};

const AuthorityCheckboxGroup: React.FC<AuthorityCheckboxGroupProps> = ({
  label,
  value,
  onChange,
  options,
  helperText,
  disabled = false,
}) => {
  const handleChange = (authority: string) => {
    if (value.includes(authority)) {
      onChange(value.filter(v => v !== authority));
    } else {
      onChange([...value, authority]);
    }
  };

  const handleSelectAll = () => {
    if (value.length === options.length) {
      onChange([]);
    } else {
      onChange([...options]);
    }
  };

  return (
    <FormControl component="fieldset" fullWidth disabled={disabled}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <FormLabel component="legend" sx={{ color: '#374151', fontWeight: 600 }}>
          {label}
        </FormLabel>
        <Chip
          label={value.length === options.length ? "Deselect All" : "Select All"}
          size="small"
          onClick={handleSelectAll}
          sx={{ cursor: 'pointer' }}
        />
      </Stack>
      
      <Box sx={{ 
        border: '1px solid #e5e7eb',
        borderRadius: 1,
        p: 2,
        backgroundColor: disabled ? '#f9fafb' : 'white',
      }}>
        <FormGroup>
          <Stack spacing={1}>
            {options.map((authority) => (
              <FormControlLabel
                key={authority}
                control={
                  <Checkbox
                    checked={value.includes(authority)}
                    onChange={() => handleChange(authority)}
                    size="small"
                  />
                }
                label={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {getAuthorityIcon(authority)}
                    <Typography variant="body2">{authority}</Typography>
                    <Chip 
                      label={authority} 
                      size="small" 
                      color={getAuthorityColor(authority)}
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  </Stack>
                }
              />
            ))}
          </Stack>
        </FormGroup>
      </Box>
      
      {helperText && (
        <FormHelperText>{helperText}</FormHelperText>
      )}
      
      {value.length > 0 && (
        <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap">
          <Typography variant="caption" color="text.secondary">
            Selected:
          </Typography>
          {value.map(auth => (
            <Chip 
              key={auth}
              label={auth} 
              size="small" 
              color={getAuthorityColor(auth)}
              onDelete={() => handleChange(auth)}
              sx={{ mb: 0.5 }}
            />
          ))}
        </Stack>
      )}
    </FormControl>
  );
};

export default AuthorityCheckboxGroup;